import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import { StackCards } from '@/components/StackCards';
import { Card } from '@/components/StackCards/types';
import IconDeferredPayment from '@/assets/icons/heroIcons/Deferred-payment.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
import IconCard from '@/assets/icons/heroIcons/Card-front.svg';
import Hero from '@/sections/Hero';
import MoreInfoSection from '@/sections/MoreInfo';
import { FaqIntro } from '@/sections/FaqIntro';
import { Button, DownloadButton } from '@/components/buttons';
import { SectionHeadline } from '@/components/SectionHeadline';
import { BenefitTiles } from '@/components/tiles';
import { ContentContainer } from '@/components/layout';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { Trans, useTranslation } from 'react-i18next';
import { LINKS } from '@/constants/links';
import { FILES } from '@/constants/files';
import React from 'react';
import { mergeClasses } from '@/utils/style';
import { NumericRange } from '@/types/range';
import Image from 'next/image';
import IconCheck from '@/assets/icons/heroIcons/Check.svg';
import { Link } from '@/components/links';
import Floating from '@/sections/Floating';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { useRouter } from 'next/router';
import { SeoHeader } from '@/components/SeoHeader';
import { Text } from '@/components/typography';
import { AnimatedSection, AnimatedContent } from '@/components/animation';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import Overlay from '@/sections/deprecatedInsurance/Overlay';
import { getSectionAnchorId } from '@/utils/anchors';
interface Props extends IntlProps {
  menuPromoBanners?: PageMenuPromoBanner[];
  pageBanner: PageBanner | null;
}
export default function Page(props) {
  const $ = _c(224);
  usePageView("insurance");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["insurance", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    pageBanner,
    menuPromoBanners
  } = props;
  const {
    locale
  } = useRouter();
  let t1;
  if ($[1] !== t) {
    t1 = Array.from({
      length: 4
    }, (_, index) => {
      const cardIndex = index + 1 as NumericRange<1, 4>;
      return {
        index,
        menuTitle: "",
        title: <div className={mergeClasses(cardIndex === 4 && "text-center lg:text-left")}>{t(`howItWorks.items.${cardIndex}.title`)}</div>,
        ...(cardIndex < 4 ? {
          description: <Trans t={t} i18nKey={`howItWorks.items.${cardIndex}.text`}><Link to={LINKS().registration} testId="insuranceRegistration" analyticsParam="insuranceRegistration" /></Trans>
        } : {
          footer: <Button analyticsParam="insuranceHowItWorksiWantMaxi" testId="insuranceHowItWorksiWantMaxi" variant="primary" size="lg" className="m-auto w-full md:w-auto lg:m-0" to={LINKS().registration}>{t(`howItWorks.items.${cardIndex}.text`)}</Button>
        }),
        images: {
          desktop: {
            src: `/images/sections/insurance/cards/cs/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.title`)
          },
          mobile: {
            src: `/images/sections/insurance/cards/cs/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.title`)
          },
          ...(cardIndex < 4 && {
            variant: "appMockup"
          })
        }
      };
    });
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const cards = t1;
  let t2;
  if ($[3] !== t) {
    t2 = t("notAvailable.title");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t) {
    t3 = t("notAvailable.text");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== locale) {
    t4 = FILES(locale);
    $[7] = locale;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4.insuranceTerms) {
    t5 = {
      text: t3,
      href: t4.insuranceTerms
    };
    $[9] = t3;
    $[10] = t4.insuranceTerms;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t2 || $[13] !== t5) {
    t6 = <Overlay title={t2} link={t5} />;
    $[12] = t2;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t) {
    t7 = t("seo.title");
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t) {
    t8 = t("seo.description");
    $[17] = t;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t7 || $[20] !== t8) {
    t9 = <SeoHeader title={t7} description={t8} nofollow={true} noindex={true} />;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = pageBanner?.videoId;
  let t11;
  if ($[22] !== pageBanner?.title || $[23] !== t) {
    t11 = pageBanner?.title ?? t("banner.title");
    $[22] = pageBanner?.title;
    $[23] = t;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== pageBanner?.text || $[26] !== t) {
    t12 = pageBanner?.text ?? t("banner.text");
    $[25] = pageBanner?.text;
    $[26] = t;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== pageBanner || $[29] !== t) {
    t13 = pageBanner ? pageBanner.buttons : [{
      ariaLabel: t("banner.cta"),
      label: t("banner.cta"),
      to: LINKS().registration,
      testId: "insuranceHeroPrimaryCTA",
      analyticsParam: "insuranceHeroPrimaryCTA",
      variant: "primary"
    }];
    $[28] = pageBanner;
    $[29] = t;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const t14 = pageBanner?.image?.url ?? "/images/sections/insurance/hero/cs/hero.png";
  let t15;
  if ($[31] !== pageBanner?.image?.alt || $[32] !== t) {
    t15 = pageBanner?.image?.alt ?? t("banner.title");
    $[31] = pageBanner?.image?.alt;
    $[32] = t;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t14 || $[35] !== t15) {
    t16 = {
      url: t14,
      alt: t15
    };
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const t17 = pageBanner?.variant || "mockupMobile";
  let t18;
  if ($[37] !== t10 || $[38] !== t11 || $[39] !== t12 || $[40] !== t13 || $[41] !== t16 || $[42] !== t17) {
    t18 = <Hero videoId={t10} title={t11} text={t12} buttons={t13} image={t16} variant={t17} />;
    $[37] = t10;
    $[38] = t11;
    $[39] = t12;
    $[40] = t13;
    $[41] = t16;
    $[42] = t17;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = getSectionAnchorId("howWeProtect");
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t) {
    t20 = t("howWeProtect.items.1.text");
    $[45] = t;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t) {
    t21 = t("howWeProtect.items.1.title");
    $[47] = t;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t) {
    t22 = t("howWeProtect.items.1.title");
    $[49] = t;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t22) {
    t23 = {
      alt: t22,
      src: "/images/sections/insurance/boxes/1.png"
    };
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== t20 || $[54] !== t21 || $[55] !== t23) {
    t24 = {
      anchorKey: "howWeProtect.items.1.title",
      variant: "small",
      content: t20,
      title: t21,
      image: t23
    };
    $[53] = t20;
    $[54] = t21;
    $[55] = t23;
    $[56] = t24;
  } else {
    t24 = $[56];
  }
  let t25;
  if ($[57] !== t) {
    t25 = t("howWeProtect.items.2.text");
    $[57] = t;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== t) {
    t26 = t("howWeProtect.items.2.title");
    $[59] = t;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== t) {
    t27 = t("howWeProtect.items.2.title");
    $[61] = t;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t27) {
    t28 = {
      alt: t27,
      src: "/images/sections/insurance/boxes/2.png"
    };
    $[63] = t27;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== t25 || $[66] !== t26 || $[67] !== t28) {
    t29 = {
      anchorKey: "howWeProtect.items.2.title",
      variant: "small",
      content: t25,
      title: t26,
      image: t28,
      imagePosition: "right"
    };
    $[65] = t25;
    $[66] = t26;
    $[67] = t28;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== t) {
    t30 = t("howWeProtect.items.3.text");
    $[69] = t;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== t) {
    t31 = t("howWeProtect.items.3.title");
    $[71] = t;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  let t32;
  if ($[73] !== t) {
    t32 = t("howWeProtect.items.3.title");
    $[73] = t;
    $[74] = t32;
  } else {
    t32 = $[74];
  }
  let t33;
  if ($[75] !== t32) {
    t33 = {
      alt: t32,
      src: "/images/sections/insurance/boxes/3.png"
    };
    $[75] = t32;
    $[76] = t33;
  } else {
    t33 = $[76];
  }
  let t34;
  if ($[77] !== t30 || $[78] !== t31 || $[79] !== t33) {
    t34 = {
      anchorKey: "howWeProtect.items.3.title",
      variant: "small",
      content: t30,
      title: t31,
      image: t33
    };
    $[77] = t30;
    $[78] = t31;
    $[79] = t33;
    $[80] = t34;
  } else {
    t34 = $[80];
  }
  let t35;
  if ($[81] !== t24 || $[82] !== t29 || $[83] !== t34) {
    t35 = [t24, t29, t34];
    $[81] = t24;
    $[82] = t29;
    $[83] = t34;
    $[84] = t35;
  } else {
    t35 = $[84];
  }
  let t36;
  if ($[85] !== t) {
    t36 = t("howWeProtect.cta");
    $[85] = t;
    $[86] = t36;
  } else {
    t36 = $[86];
  }
  let t37;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = LINKS();
    $[87] = t37;
  } else {
    t37 = $[87];
  }
  let t38;
  if ($[88] !== t36) {
    t38 = {
      analyticsParam: "insuranceRegistration",
      testId: "insuranceRegistration",
      text: t36,
      to: t37.registration
    };
    $[88] = t36;
    $[89] = t38;
  } else {
    t38 = $[89];
  }
  let t39;
  if ($[90] !== t) {
    t39 = t("howWeProtect.title");
    $[90] = t;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  let t40;
  if ($[92] !== t35 || $[93] !== t38 || $[94] !== t39) {
    t40 = <AnimatedContent className="relative z-30 pb-14 lg:pb-20"><Floating boxes={t35} button={t38} title={t39} /></AnimatedContent>;
    $[92] = t35;
    $[93] = t38;
    $[94] = t39;
    $[95] = t40;
  } else {
    t40 = $[95];
  }
  let t41;
  if ($[96] !== t) {
    t41 = t("howItWorks.title");
    $[96] = t;
    $[97] = t41;
  } else {
    t41 = $[97];
  }
  let t42;
  if ($[98] !== t41) {
    t42 = <SectionHeadline className="mb-12 px-4 md:mb-16" anchorKey="howItWorks.title">{t41}</SectionHeadline>;
    $[98] = t41;
    $[99] = t42;
  } else {
    t42 = $[99];
  }
  let t43;
  if ($[100] !== cards) {
    t43 = <StackCards imagePosition="left" cards={cards} />;
    $[100] = cards;
    $[101] = t43;
  } else {
    t43 = $[101];
  }
  let t44;
  if ($[102] !== t42 || $[103] !== t43) {
    t44 = <div id="how-it-works" className="scroll-mt-20"><AnimatedContent className="relative pt-10 pb-14 lg:pt-20 lg:pb-32">{t42}{t43}</AnimatedContent></div>;
    $[102] = t42;
    $[103] = t43;
    $[104] = t44;
  } else {
    t44 = $[104];
  }
  let t45;
  if ($[105] !== t40 || $[106] !== t44) {
    t45 = <AnimatedSection bgColor="bg-blue-100" id={t19}>{t40}{t44}</AnimatedSection>;
    $[105] = t40;
    $[106] = t44;
    $[107] = t45;
  } else {
    t45 = $[107];
  }
  let t46;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = getSectionAnchorId("fairRules");
    $[108] = t46;
  } else {
    t46 = $[108];
  }
  let t47;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <div className="bgCircle absolute right-0 bottom-0 hidden translate-x-1/2 -translate-y-1/4 rotate-90 lg:block" />;
    $[109] = t47;
  } else {
    t47 = $[109];
  }
  let t48;
  if ($[110] !== t) {
    t48 = t("fairRules.title");
    $[110] = t;
    $[111] = t48;
  } else {
    t48 = $[111];
  }
  let t49;
  if ($[112] !== t48) {
    t49 = <div className="relative z-10 mx-auto w-full max-w-[61rem] text-center"><SectionHeadline className="mb-4" anchorKey="fairRules.title">{t48}</SectionHeadline></div>;
    $[112] = t48;
    $[113] = t49;
  } else {
    t49 = $[113];
  }
  let t50;
  if ($[114] !== t) {
    t50 = <div className="flex flex-col gap-4"><BenefitTiles classNameItem="md:h-[7.5rem]" items={Array.from({
        length: 6
      }, (__0, index_0) => {
        const itemIndex = index_0 + 1 as NumericRange<1, 6>;
        return {
          title: t(`fairRules.items.${itemIndex}`)
        };
      })} /></div>;
    $[114] = t;
    $[115] = t50;
  } else {
    t50 = $[115];
  }
  let t51;
  if ($[116] !== t) {
    t51 = t("fairRules.text");
    $[116] = t;
    $[117] = t51;
  } else {
    t51 = $[117];
  }
  let t52;
  if ($[118] !== t51) {
    t52 = <Text variant="p4" className="mx-auto my-10 max-w-[48rem] text-center text-gray-600 lg:my-12">{t51}</Text>;
    $[118] = t51;
    $[119] = t52;
  } else {
    t52 = $[119];
  }
  let t53;
  if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = LINKS();
    $[120] = t53;
  } else {
    t53 = $[120];
  }
  const t54 = t53.registration;
  let t55;
  if ($[121] !== t) {
    t55 = t("fairRules.cta");
    $[121] = t;
    $[122] = t55;
  } else {
    t55 = $[122];
  }
  let t56;
  if ($[123] !== t55) {
    t56 = <Button to={t54} testId="insuranceRevolving" analyticsParam="insuranceRevolving" size="lg" variant="primary" align="center">{t55}</Button>;
    $[123] = t55;
    $[124] = t56;
  } else {
    t56 = $[124];
  }
  let t57;
  if ($[125] !== t50 || $[126] !== t52 || $[127] !== t56) {
    t57 = <div className="relativ z-10 mx-auto mt-8 max-w-[61rem] md:mt-16">{t50}{t52}{t56}</div>;
    $[125] = t50;
    $[126] = t52;
    $[127] = t56;
    $[128] = t57;
  } else {
    t57 = $[128];
  }
  let t58;
  if ($[129] !== t49 || $[130] !== t57) {
    t58 = <AnimatedContent className="relative z-30 pb-10 lg:pb-20"><div className="relative overflow-hidden">{t47}{t49}{t57}</div></AnimatedContent>;
    $[129] = t49;
    $[130] = t57;
    $[131] = t58;
  } else {
    t58 = $[131];
  }
  let t59;
  if ($[132] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = <div className="bgCircle absolute top-0 left-0 hidden h-[24rem] w-[24rem] translate-x-1/4 translate-y-20 rotate-90 lg:block" />;
    $[132] = t59;
  } else {
    t59 = $[132];
  }
  let t60;
  if ($[133] !== t) {
    t60 = t("examples.title");
    $[133] = t;
    $[134] = t60;
  } else {
    t60 = $[134];
  }
  let t61;
  if ($[135] !== t60) {
    t61 = <div className="mx-auto w-full max-w-[50rem] text-center"><SectionHeadline className="mb-5" anchorKey="examples.title">{t60}</SectionHeadline></div>;
    $[135] = t60;
    $[136] = t61;
  } else {
    t61 = $[136];
  }
  let t62;
  if ($[137] !== t) {
    t62 = t("examples.text");
    $[137] = t;
    $[138] = t62;
  } else {
    t62 = $[138];
  }
  let t63;
  if ($[139] !== t62) {
    t63 = <Text variant="p2" className="mb-12 text-center text-gray-800 lg:mb-16">{t62}</Text>;
    $[139] = t62;
    $[140] = t63;
  } else {
    t63 = $[140];
  }
  let t64;
  if ($[141] !== t) {
    t64 = <div className="flex flex-col gap-4">{Array.from({
        length: 10
      }, (__1, index_1) => {
        const itemIndex_0 = index_1 + 1 as NumericRange<1, 10>;
        return <div className="flex flex-row justify-between gap-4 align-top" key={itemIndex_0}><Text variant="p2" className="text-left">{t(`examples.items.${itemIndex_0}`)}</Text><div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-500/10"><IconCheck className="h-5 w-5 text-green-500" /></div></div>;
      })}</div>;
    $[141] = t;
    $[142] = t64;
  } else {
    t64 = $[142];
  }
  let t65;
  if ($[143] !== locale) {
    t65 = FILES(locale);
    $[143] = locale;
    $[144] = t65;
  } else {
    t65 = $[144];
  }
  const t66 = t65.insuranceTerms;
  let t67;
  if ($[145] !== t) {
    t67 = t("examples.file");
    $[145] = t;
    $[146] = t67;
  } else {
    t67 = $[146];
  }
  let t68;
  if ($[147] !== t65.insuranceTerms || $[148] !== t67) {
    t68 = <div className="text-center"><DownloadButton to={t66} testId="insuranceConditions" analyticsParam="insuranceConditions" className="mt-12 lg:mt-16">{t67}</DownloadButton></div>;
    $[147] = t65.insuranceTerms;
    $[148] = t67;
    $[149] = t68;
  } else {
    t68 = $[149];
  }
  let t69;
  if ($[150] !== t64 || $[151] !== t68) {
    t69 = <div className="mx-auto mt-8 max-w-[35.5rem] md:mt-16">{t64}{t68}</div>;
    $[150] = t64;
    $[151] = t68;
    $[152] = t69;
  } else {
    t69 = $[152];
  }
  let t70;
  if ($[153] !== t61 || $[154] !== t63 || $[155] !== t69) {
    t70 = <AnimatedContent className="relative px-4">{t59}<div className="relative z-10">{t61}{t63}{t69}</div></AnimatedContent>;
    $[153] = t61;
    $[154] = t63;
    $[155] = t69;
    $[156] = t70;
  } else {
    t70 = $[156];
  }
  let t71;
  if ($[157] !== t58 || $[158] !== t70) {
    t71 = <AnimatedSection bgColor="bg-blue-50" id={t46}>{t58}{t70}</AnimatedSection>;
    $[157] = t58;
    $[158] = t70;
    $[159] = t71;
  } else {
    t71 = $[159];
  }
  let t72;
  if ($[160] === Symbol.for("react.memo_cache_sentinel")) {
    t72 = getSectionAnchorId("csob");
    $[160] = t72;
  } else {
    t72 = $[160];
  }
  let t73;
  if ($[161] !== t) {
    t73 = t("csob.title");
    $[161] = t;
    $[162] = t73;
  } else {
    t73 = $[162];
  }
  let t74;
  if ($[163] !== t73) {
    t74 = <div className="w-full shrink"><Image src="/images/sections/insurance/cs/csob.png" alt={t73} width={615} height={656} className="md:max-w-fullg mx-auto max-w-[90%]" /></div>;
    $[163] = t73;
    $[164] = t74;
  } else {
    t74 = $[164];
  }
  let t75;
  if ($[165] === Symbol.for("react.memo_cache_sentinel")) {
    t75 = <span className="text-blue-500" />;
    $[165] = t75;
  } else {
    t75 = $[165];
  }
  let t76;
  if ($[166] !== t) {
    t76 = <SectionHeadline className="mb-5 lg:text-left" anchorKey="csob.title"><Trans t={t} i18nKey="csob.title">{t75}</Trans></SectionHeadline>;
    $[166] = t;
    $[167] = t76;
  } else {
    t76 = $[167];
  }
  let t77;
  if ($[168] !== t) {
    t77 = t("csob.text");
    $[168] = t;
    $[169] = t77;
  } else {
    t77 = $[169];
  }
  let t78;
  if ($[170] !== t77) {
    t78 = <Text variant="p2" className="text-center text-gray-800 lg:text-left">{t77}</Text>;
    $[170] = t77;
    $[171] = t78;
  } else {
    t78 = $[171];
  }
  let t79;
  if ($[172] !== t76 || $[173] !== t78) {
    t79 = <div className="w-[29.25rem] max-w-full shrink-0">{t76}{t78}</div>;
    $[172] = t76;
    $[173] = t78;
    $[174] = t79;
  } else {
    t79 = $[174];
  }
  let t80;
  if ($[175] !== t74 || $[176] !== t79) {
    t80 = <AnimatedContent className="relative z-10 px-4 pb-10 lg:pb-20"><ContentContainer><div className="flex flex-col items-center gap-12 lg:flex-row lg:gap-10">{t74}{t79}</div></ContentContainer></AnimatedContent>;
    $[175] = t74;
    $[176] = t79;
    $[177] = t80;
  } else {
    t80 = $[177];
  }
  let t81;
  if ($[178] === Symbol.for("react.memo_cache_sentinel")) {
    t81 = LINKS();
    $[178] = t81;
  } else {
    t81 = $[178];
  }
  let t82;
  if ($[179] === Symbol.for("react.memo_cache_sentinel")) {
    t82 = <Link to="/caste-dotazy/pojisteni-nakupu" analyticsParam="faqInsurance" testId="faqInsurance" />;
    $[179] = t82;
  } else {
    t82 = $[179];
  }
  let t83;
  if ($[180] !== t) {
    t83 = <AnimatedContent className="py-10 lg:py-20"><FaqIntro link={t81.faq} text={<Trans i18nKey="faq.text" t={t}>{t82}</Trans>} /></AnimatedContent>;
    $[180] = t;
    $[181] = t83;
  } else {
    t83 = $[181];
  }
  let t84;
  if ($[182] === Symbol.for("react.memo_cache_sentinel")) {
    t84 = <IconDeferredPayment className="fillButtonIcon" />;
    $[182] = t84;
  } else {
    t84 = $[182];
  }
  let t85;
  if ($[183] !== t) {
    t85 = t("core:link.deferredPayment");
    $[183] = t;
    $[184] = t85;
  } else {
    t85 = $[184];
  }
  let t86;
  if ($[185] === Symbol.for("react.memo_cache_sentinel")) {
    t86 = LINKS();
    $[185] = t86;
  } else {
    t86 = $[185];
  }
  let t87;
  if ($[186] !== t85) {
    t87 = {
      analyticsParam: "insuranceMoreInfoDeferredPayment",
      icon: t84,
      testId: "insuranceMoreInfoDeferredPayment",
      title: t85,
      to: t86.deferredPayment
    };
    $[186] = t85;
    $[187] = t87;
  } else {
    t87 = $[187];
  }
  let t88;
  if ($[188] === Symbol.for("react.memo_cache_sentinel")) {
    t88 = <IconMaxi className="fillButtonIcon" />;
    $[188] = t88;
  } else {
    t88 = $[188];
  }
  let t89;
  if ($[189] !== t) {
    t89 = t("core:products.revolving");
    $[189] = t;
    $[190] = t89;
  } else {
    t89 = $[190];
  }
  let t90;
  if ($[191] === Symbol.for("react.memo_cache_sentinel")) {
    t90 = LINKS();
    $[191] = t90;
  } else {
    t90 = $[191];
  }
  let t91;
  if ($[192] !== t89) {
    t91 = {
      analyticsParam: "insuranceMoreInfoRevolvingApplication",
      icon: t88,
      testId: "insuranceMoreInfoRevolvingApplication",
      title: t89,
      to: t90.revolving
    };
    $[192] = t89;
    $[193] = t91;
  } else {
    t91 = $[193];
  }
  let t92;
  if ($[194] === Symbol.for("react.memo_cache_sentinel")) {
    t92 = <IconThird className="fillButtonIcon" />;
    $[194] = t92;
  } else {
    t92 = $[194];
  }
  let t93;
  if ($[195] !== t) {
    t93 = t("core:products.payInThree");
    $[195] = t;
    $[196] = t93;
  } else {
    t93 = $[196];
  }
  let t94;
  if ($[197] === Symbol.for("react.memo_cache_sentinel")) {
    t94 = LINKS();
    $[197] = t94;
  } else {
    t94 = $[197];
  }
  let t95;
  if ($[198] !== t93) {
    t95 = {
      analyticsParam: "insuranceMoreInfoPayInThree",
      icon: t92,
      testId: "insuranceMoreInfoPayInThree",
      title: t93,
      to: t94.payInThree
    };
    $[198] = t93;
    $[199] = t95;
  } else {
    t95 = $[199];
  }
  let t96;
  if ($[200] === Symbol.for("react.memo_cache_sentinel")) {
    t96 = <IconCard className="fillButtonIcon" />;
    $[200] = t96;
  } else {
    t96 = $[200];
  }
  let t97;
  if ($[201] !== t) {
    t97 = t("core:products.card");
    $[201] = t;
    $[202] = t97;
  } else {
    t97 = $[202];
  }
  let t98;
  if ($[203] === Symbol.for("react.memo_cache_sentinel")) {
    t98 = LINKS();
    $[203] = t98;
  } else {
    t98 = $[203];
  }
  let t99;
  if ($[204] !== t97) {
    t99 = {
      analyticsParam: "insuranceMoreInfoCard",
      icon: t96,
      testId: "insuranceMoreInfoCard",
      title: t97,
      to: t98.card
    };
    $[204] = t97;
    $[205] = t99;
  } else {
    t99 = $[205];
  }
  let t100;
  if ($[206] !== t87 || $[207] !== t91 || $[208] !== t95 || $[209] !== t99) {
    t100 = <AnimatedContent><MoreInfoSection buttons={[t87, t91, t95, t99]} /></AnimatedContent>;
    $[206] = t87;
    $[207] = t91;
    $[208] = t95;
    $[209] = t99;
    $[210] = t100;
  } else {
    t100 = $[210];
  }
  let t101;
  if ($[211] === Symbol.for("react.memo_cache_sentinel")) {
    t101 = <div className="bgCircle absolute right-0 bottom-0 z-[-1] translate-x-1/4 translate-y-1/4 rotate-180" />;
    $[211] = t101;
  } else {
    t101 = $[211];
  }
  let t102;
  if ($[212] !== t100 || $[213] !== t80 || $[214] !== t83) {
    t102 = <AnimatedSection bgColor="bg-blue-100" className="overflow-hidden" id={t72}>{t80}{t83}{t100}{t101}</AnimatedSection>;
    $[212] = t100;
    $[213] = t80;
    $[214] = t83;
    $[215] = t102;
  } else {
    t102 = $[215];
  }
  let t103;
  if ($[216] !== menuPromoBanners || $[217] !== t102 || $[218] !== t18 || $[219] !== t45 || $[220] !== t6 || $[221] !== t71 || $[222] !== t9) {
    t103 = <Layout menuPromoBanners={menuPromoBanners}>{t6}{t9}{t18}{t45}{t71}{t102}</Layout>;
    $[216] = menuPromoBanners;
    $[217] = t102;
    $[218] = t18;
    $[219] = t45;
    $[220] = t6;
    $[221] = t71;
    $[222] = t9;
    $[223] = t103;
  } else {
    t103 = $[223];
  }
  return t103;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('pojisteni-ke-karte');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      pageBanner: await getPageBannerBySlug('insurance'),
      ...(await getIntlProps(ctx, ['core', 'insurance']))
    }
  };
}) satisfies GetStaticProps<Props>;